// @ts-nocheck

import React, { useState, useEffect, useRef } from 'react';
import { collectionGroup, orderBy, query, limit, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { MediaItem, getThumbnail } from '../types';
import Modal from '../components/Modal';
import { downloadAndStoreMedia } from '../utils/mediaDownloader';

type TabType = 'all' | 'approved' | 'rejected';

// Helper function to check if URL needs proxying
const needsProxy = (url: string): boolean => {
  if (!url) return false;
  // Don't proxy Firebase Storage URLs or Sellox CDN URLs
  if (url.includes('firebasestorage') || url.includes('sellox')) return false;
  // Proxy Instagram URLs
  if (url.includes('instagram') || url.includes('cdninstagram')) return true;
  return false;
};

// Helper function to get proxied URL with proper resource fetching
const getProxiedUrl = async (url: string): Promise<string> => {
  if (!url) return '';

  // Skip proxying for Firebase Storage URLs
  if (!needsProxy(url)) {
    return url;
  }

  try {
    const response = await fetch(`https://api.sellox.io/api/proxy?url=${encodeURIComponent(url)}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching proxied resource:', error);
    return url; // Fallback to original URL
  }
};

// Cache for proxied URLs to avoid refetching
const urlCache = new Map<string, string>();

// Helper function to get cached proxied URL
const getCachedProxiedUrl = async (url: string): Promise<string> => {
  if (!url) return '';

  if (urlCache.has(url)) {
    return urlCache.get(url)!;
  }

  const proxiedUrl = await getProxiedUrl(url);
  urlCache.set(url, proxiedUrl);
  return proxiedUrl;
};

const PinsPage: React.FC = () => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<MediaItem[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadingItems, setDownloadingItems] = useState<Set<string>>(new Set());
  const loadedImagesRef = useRef<Map<string, string>>(new Map());
  const selectedItemIdRef = useRef<string | null>(null);
  const { currentUser } = useAuth();
  const [isGlobalDownloading, setIsGlobalDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const cancelDownloadRef = useRef(false);

  // Preload images for better UX
  const preloadImages = async (postsData: MediaItem[]) => {
    const imagePromises = postsData.map(async (post) => {
      try {
        const thumbnail = getThumbnail(post, 'grid');
        const proxiedUrl = await getCachedProxiedUrl(thumbnail);
        loadedImagesRef.current.set(post.id, proxiedUrl);
      } catch (error) {
        console.error(`Error preloading image for post ${post.id}:`, error);
      }
    });

    // Load images in batches to avoid overwhelming the browser
    const batchSize = 10;
    for (let i = 0; i < imagePromises.length; i += batchSize) {
      const batch = imagePromises.slice(i, i + batchSize);
      await Promise.allSettled(batch);
    }
  };

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupRealtimeListener = async () => {
      try {
        if (!currentUser) {
          setLoading(false);
          return;
        }

        // Fetch from IGPosts collection group (like posts page)
        const q = query(
          collectionGroup(db, 'IGPosts'),
          orderBy('createdAt', 'desc'),
          limit(300)
        );

        // Set up realtime listener
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const fetchedItems: MediaItem[] = [];
          querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();

            // Build media array based on post type (like posts page)
            let mediaArray: MediaFile[] = [];

            if (data.type === 'Video' && data.videoUrl) {
              // Video posts: thumbnail + video
              mediaArray = [
                {
                  downloadURL: data.displayUrl || '',
                  type: 'image',
                  primary: true
                },
                {
                  downloadURL: data.videoUrl,
                  type: 'video',
                  path: data.videoUrl
                }
              ];
            } else if (data.type === 'Sidecar' && data.childPosts && data.childPosts.length > 0) {
              // Sidecar posts: multiple images/videos from childPosts
              mediaArray = data.childPosts.map((child: any, index: number) => {
                if (child.type === 'Video' && child.videoUrl) {
                  return {
                    downloadURL: child.videoUrl,
                    type: 'video',
                    path: child.videoUrl,
                    primary: index === 0
                  };
                }
                return {
                  downloadURL: child.displayUrl || child.url || '',
                  type: 'image',
                  primary: index === 0
                };
              });
            } else {
              // Image posts: just the display image
              mediaArray = [
                {
                  downloadURL: data.displayUrl || '',
                  type: 'image',
                  primary: true
                }
              ];
            }

            fetchedItems.push({
              id: docSnap.id,
              docPath: docSnap.ref.path, // Store full path for updates
              name: docSnap.id,
              description: data.caption || '',
              caption: data.caption || '',
              createdAt: data.createdAt,
              type: data.type || 'image',
              url: data.displayUrl || '',
              media: mediaArray,
              childPosts: data.childPosts || undefined,
              startAt: data.startAt,
              endAt: data.endAt,
              status: data.status || undefined,
              mediaDownloaded: data.mediaDownloaded || false
            } as MediaItem);
          });
          setItems(fetchedItems);
          setFilteredItems(fetchedItems);

          // Preload images in background
          preloadImages(fetchedItems);
          setLoading(false);
        }, (error) => {
          console.error('Error in realtime listener:', error);
          setLoading(false);
        });

      } catch (error) {
        console.error('Error setting up realtime listener:', error);
        setLoading(false);
      }
    };

    setupRealtimeListener();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  useEffect(() => {
    const filterItems = () => {
      switch (activeTab) {
        case 'all':
          return items.filter(item => !item.status || item.status === '');
        case 'approved':
          return items.filter(item => item.status === 'approved');
        case 'rejected':
          return items.filter(item => item.status === 'rejected');
        default:
          return items;
      }
    };

    setFilteredItems(filterItems().sort((itemA, itemB) => itemA.createdAt - itemB.createdAt));
  }, [activeTab, items]);

  // Update selectedItem when items change (to reflect downloaded media URLs)
  useEffect(() => {
    if (selectedItemIdRef.current && isModalOpen) {
      const updatedItem = items.find(item => item.id === selectedItemIdRef.current);
      if (updatedItem) {
        setSelectedItem(updatedItem);
      }
    }
  }, [items, isModalOpen]);

  const handleItemClick = (item: MediaItem) => {
    selectedItemIdRef.current = item.id;
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    selectedItemIdRef.current = null;
  };

  const handleDownloadMedia = async (item: MediaItem, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent opening the modal
    }

    if (downloadingItems.has(item.id) || item.mediaDownloaded) {
      return;
    }

    setDownloadingItems(prev => new Set(prev).add(item.id));

    try {
      await downloadAndStoreMedia(item);
      console.log(`Successfully downloaded media for post ${item.id}`);
      // The realtime listener will automatically update the item with mediaDownloaded flag
    } catch (error) {
      console.error('Error downloading media:', error);
      if (event) {
        // Only show alert if manually triggered
        alert('Failed to download media. Please try again.');
      }
    } finally {
      setDownloadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  // Global download all function
  const handleDownloadAll = async () => {
    if (isGlobalDownloading) return;

    const postsNeedingDownload = items.filter(
      item => !item.mediaDownloaded && !downloadingItems.has(item.id) && (!item.status || item.status === '')
    );

    if (postsNeedingDownload.length === 0) {
      alert('All posts have already been downloaded!');
      return;
    }

    const confirmed = window.confirm(
      `Download media for ${postsNeedingDownload.length} posts? This may take a while.`
    );

    if (!confirmed) return;

    cancelDownloadRef.current = false;
    setIsGlobalDownloading(true);
    setDownloadProgress({ current: 0, total: postsNeedingDownload.length });
    console.log(`Starting global download for ${postsNeedingDownload.length} posts...`);

    try {
      // Process in batches of 3 to avoid overwhelming the system
      const batchSize = 3;
      let processed = 0;

      for (let i = 0; i < postsNeedingDownload.length; i += batchSize) {
        // Check if download was cancelled
        if (cancelDownloadRef.current) {
          console.log('Download cancelled by user');
          alert(`Download stopped. ${processed} of ${postsNeedingDownload.length} posts downloaded.`);
          break;
        }

        const batch = postsNeedingDownload.slice(i, i + batchSize);

        // Download batch concurrently
        await Promise.allSettled(
          batch.map(item => handleDownloadMedia(item))
        );

        processed += batch.length;
        setDownloadProgress({ current: processed, total: postsNeedingDownload.length });

        // Wait a bit between batches to avoid rate limiting
        if (i + batchSize < postsNeedingDownload.length && !cancelDownloadRef.current) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (!cancelDownloadRef.current) {
        console.log('Global download completed');
        alert('All media downloaded successfully!');
      }
    } catch (error) {
      console.error('Error in global download:', error);
      alert('Some downloads failed. Check console for details.');
    } finally {
      setIsGlobalDownloading(false);
      setDownloadProgress({ current: 0, total: 0 });
      cancelDownloadRef.current = false;
    }
  };

  const handleStopDownload = () => {
    cancelDownloadRef.current = true;
  };

  // Component for handling proxied image loading
  const ProxiedImage = ({ item, onClick }: { item: MediaItem; onClick: () => void }) => {
    const [imageSrc, setImageSrc] = useState<string>(() => {
      // Initialize with cached value if available
      return loadedImagesRef.current.get(item.id) || '';
    });
    const [isImageLoading, setIsImageLoading] = useState(() => {
      // If we have cached image, don't show loading
      return !loadedImagesRef.current.has(item.id);
    });

    useEffect(() => {
      // Skip if already loaded and cached
      if (loadedImagesRef.current.has(item.id) && imageSrc) {
        return;
      }

      const loadImage = async () => {
        try {
          setIsImageLoading(true);

          // Get thumbnail URL first
          const thumbnailUrl = getThumbnail(item, 'grid');

          // Load image through proxy
          const proxiedUrl = await getCachedProxiedUrl(thumbnailUrl);
          setImageSrc(proxiedUrl);
          loadedImagesRef.current.set(item.id, proxiedUrl);
        } catch (error) {
          console.error(`Error loading image for post ${item.id}:`, error);
          // Fallback to original URL
          const fallbackUrl = getThumbnail(item, 'grid');
          setImageSrc(fallbackUrl);
        } finally {
          setIsImageLoading(false);
        }
      };

      loadImage();
    }, [item.id]);

    return (
      <div
        className="relative overflow-hidden rounded-2xl"
        onClick={onClick}
      >
        {/* Aspect ratio container to prevent layout shift */}
        <div className="relative" style={{ aspectRatio: '3/4', minHeight: '200px' }}>
          {isImageLoading ? (
            <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
              <div className="text-gray-400 text-sm">Loading...</div>
            </div>
          ) : (
            <>
              <img
                src={imageSrc}
                alt={item.name || 'Post'}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  // Fallback SVG on error
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgMTIwQzEwNS41MjMgMTIwIDExMCAxMTUuNTIzIDExMCAxMTBDMTEwIDEwNC40NzcgMTA1LjUyMyAxMDAgMTAwIDEwMEM5NC40NzcgMTAwIDkwIDEwNC40NzcgOTAgMTEwQzkwIDExNS41MjMgOTQuNDc3IDEyMCAxMDAgMTIwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                }}
              />

              {/* Media type indicator */}
              {item.type === 'Video' && (
                <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <div className="bg-white bg-opacity-90 rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                    <svg className="w-6 h-6 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
              {item.type === 'Sidecar' && (
                <div className="absolute bottom-2 right-2">
                  <div className="bg-white bg-opacity-90 rounded-full p-1">
                    <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                    </svg>
                  </div>
                </div>
              )}
              {(!item.type || item.type === 'image') && (
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <button className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    Save
                  </button>
                </div>
              )}

              {/* Download status indicator */}
              <div className="absolute top-1 left-1">
                {downloadingItems.has(item.id) ? (
                  <div className="bg-black bg-opacity-30 rounded-full p-1">
                    <svg className="w-3 h-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : item.mediaDownloaded ? (
                  <div className="bg-green-500 bg-opacity-40 rounded-full p-1">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Check if current user is authorized to see download button
  const isAuthorizedUser = currentUser?.email === 'jobizzness@sellox.io';

  return (
    <div>
      {/* Global Download Button */}
      {isAuthorizedUser && (
        <div className="fixed bottom-8 right-8 z-40 flex flex-col items-end gap-3">
          {/* Stop Button (only visible when downloading) */}
          {isGlobalDownloading && (
            <button
              onClick={handleStopDownload}
              className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-full shadow-lg transition-all duration-200 hover:scale-105 flex items-center gap-2 text-sm"
              title="Stop download"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Stop
            </button>
          )}

          {/* Main Download Button */}
          <div className="relative">
            <button
              onClick={handleDownloadAll}
              disabled={isGlobalDownloading}
              className={`${isGlobalDownloading
                ? 'bg-blue-600'
                : 'bg-red-600 hover:bg-red-700 hover:scale-110'
                } text-white p-4 rounded-full shadow-2xl transition-all duration-200 flex items-center gap-2 relative`}
              title={isGlobalDownloading ? `Downloading ${downloadProgress.current}/${downloadProgress.total}` : "Download all media"}
            >
              {isGlobalDownloading ? (
                <>
                  <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-xs font-medium">{downloadProgress.current}/{downloadProgress.total}</span>
                </>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
            </button>

            {/* Progress ring */}
            {isGlobalDownloading && downloadProgress.total > 0 && (
              <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none">
                <circle
                  cx="50%"
                  cy="50%"
                  r="30"
                  stroke="white"
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray={`${(downloadProgress.current / downloadProgress.total) * 188.5} 188.5`}
                  className="transition-all duration-300"
                  opacity="0.3"
                />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`${activeTab === 'all' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            All Posts ({items.filter(item => !item.status || item.status === '').length})
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`${activeTab === 'approved' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Approved ({items.filter(item => item.status === 'approved').length})
          </button>
          <button
            onClick={() => setActiveTab('rejected')}
            className={`${activeTab === 'rejected' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Rejected ({items.filter(item => item.status === 'rejected').length})
          </button>
        </nav>
      </div>

      {/* Pinterest-style Masonry Grid */}
      <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-6 py-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pins yet</h3>
            <p className="text-gray-500">Add some media to see your collection!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 sm:gap-3 md:gap-4">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="cursor-pointer group"
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col">
                  {/* Use ProxiedImage component */}
                  <ProxiedImage
                    item={item}
                    onClick={() => handleItemClick(item)}
                  />

                  {/* Always render container to prevent layout shift */}
                  <div className="p-3 flex-shrink-0" style={{ minHeight: '60px' }}>
                    {item.name && (
                      <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2 leading-tight">
                        {item.name}
                      </h3>
                    )}
                    {item.description && (
                      <p className="text-gray-600 text-xs line-clamp-2 leading-relaxed">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default PinsPage;
